import { useMemo } from 'react';
import { Button } from '@/components/Button';
import { MdLinkOff, MdPerson, MdEdit, MdDelete, MdNewLabel, MdLabelOff } from 'react-icons/md';
import BulkActionsPopover, {
  type BulkActionType,
  type BulkActionItem,
} from '@/components/BulkActionsPopover';
import useFetchIam from '@/features/agent/api/useFetchIam';

export type { BulkActionType };

interface Props {
  selectedCount: number;
  onDeselectAll: () => void;
  onBulkAction: (action: BulkActionType) => void;
}

const allBulkActionItems: (BulkActionItem & { requiresAdmin?: boolean })[] = [
  {
    action: 'changeOrganizationUser',
    label: '担当者変更',
    icon: <MdPerson className="size-icon-xs" />,
  },
  { action: 'addTag', label: 'タグを追加', icon: <MdNewLabel className="size-icon-xs" /> },
  {
    action: 'removeTag',
    label: 'タグを解除',
    icon: <MdLabelOff className="size-icon-xs" />,
  },
  { action: 'updateData', label: 'データ更新', icon: <MdEdit className="size-icon-xs" /> },
  {
    action: 'deleteData',
    label: 'データ削除',
    icon: <MdDelete className="size-icon-xs" />,
    requiresAdmin: true,
  },
];

export default function CompaniesSelectionBar({
  selectedCount,
  onDeselectAll,
  onBulkAction,
}: Props) {
  const { data: iam } = useFetchIam();
  const isAdmin = iam?.current_user?.is_admin || false;
  const bulkActionItems = useMemo(() => {
    return allBulkActionItems.filter((item) => !item.requiresAdmin || isAdmin);
  }, [isAdmin]);

  if (selectedCount === 0) return null;

  return (
    <div className="sticky bottom-0 z-50 flex items-center justify-between gap-2 rounded-t bg-white px-6 py-4 shadow-layer3">
      {/* 左側: 選択件数 + 選択解除 */}
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-bold">{selectedCount}件</span>
        <span className="mr-1 text-sm">選択中</span>
        <Button color="secondary" size="md" onClick={onDeselectAll}>
          <MdLinkOff className="size-icon-sm" />
          選択解除
        </Button>
      </div>

      {/* 右側: 一括操作ドロップダウン */}
      <div className="flex items-center gap-2">
        <BulkActionsPopover items={bulkActionItems} onAction={onBulkAction} />
      </div>
    </div>
  );
}
